import React from 'react';
import { ACTION_SUBJECT } from '@atlaskit/editor-common/analytics';
import { ErrorBoundary } from '@atlaskit/editor-common/error-boundary';
import { akEditorFloatingPanelZIndex } from '@atlaskit/editor-shared-styles';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import FloatingContextualButton from './FloatingContextualButton';
import FloatingContextualMenu from './FloatingContextualMenu';
import FloatingDeleteButton from './FloatingDeleteButton';
import FloatingDragMenu from './FloatingDragMenu';
// Ignored via go/ees005
// eslint-disable-next-line import/no-named-as-default
import FloatingInsertButton from './FloatingInsertButton';
import { FloatingToolbarLabel } from './FloatingToolbarLabel/FloatingToolbarLabel';
import { GlobalStylesWrapper } from './global-styles';
import { useInternalTablePluginStateSelector } from './hooks/useInternalTablePluginStateSelector';
import { FullWidthDisplay } from './TableFullWidthLabel';
var ContentComponentInternal = function ContentComponentInternal(_ref) {
  var _api$analytics, _api$accessibilityUti;
  var api = _ref.api,
    editorView = _ref.editorView,
    dispatchAnalyticsEvent = _ref.dispatchAnalyticsEvent,
    options = _ref.options,
    popupsMountPoint = _ref.popupsMountPoint,
    popupsBoundariesElement = _ref.popupsBoundariesElement,
    popupsScrollableElement = _ref.popupsScrollableElement,
    defaultGetEditorContainerWidth = _ref.defaultGetEditorContainerWidth,
    defaultGetEditorFeatureFlags = _ref.defaultGetEditorFeatureFlags;
  var editorAnalyticsAPI = api === null || api === void 0 || (_api$analytics = api.analytics) === null || _api$analytics === void 0 ? void 0 : _api$analytics.actions;
  var ariaNotifyPlugin = api === null || api === void 0 || (_api$accessibilityUti = api.accessibilityUtils) === null || _api$accessibilityUti === void 0 ? void 0 : _api$accessibilityUti.actions.ariaNotify;
  var resizingTableLocalId = useInternalTablePluginStateSelector(api, 'resizingTableLocalId', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var resizingTableRef = useInternalTablePluginStateSelector(api, 'resizingTableRef', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isTableResizing = useInternalTablePluginStateSelector(api, 'isTableResizing', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isResizing = useInternalTablePluginStateSelector(api, 'isResizing', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var widthToWidest = useInternalTablePluginStateSelector(api, 'widthToWidest', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var tableNode = useInternalTablePluginStateSelector(api, 'tableNode', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var targetCellPosition = useInternalTablePluginStateSelector(api, 'targetCellPosition', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isContextualMenuOpen = useInternalTablePluginStateSelector(api, 'isContextualMenuOpen', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var tableRef = useInternalTablePluginStateSelector(api, 'tableRef', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var pluginConfig = useInternalTablePluginStateSelector(api, 'pluginConfig', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var insertColumnButtonIndex = useInternalTablePluginStateSelector(api, 'insertColumnButtonIndex', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var insertRowButtonIndex = useInternalTablePluginStateSelector(api, 'insertRowButtonIndex', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isHeaderColumnEnabled = useInternalTablePluginStateSelector(api, 'isHeaderColumnEnabled', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isHeaderRowEnabled = useInternalTablePluginStateSelector(api, 'isHeaderRowEnabled', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isDragAndDropEnabled = useInternalTablePluginStateSelector(api, 'isDragAndDropEnabled', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var tableWrapperTarget = useInternalTablePluginStateSelector(api, 'tableWrapperTarget', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isCellMenuOpenByKeyboard = useInternalTablePluginStateSelector(api, 'isCellMenuOpenByKeyboard', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var _ref2 = pluginConfig !== null && pluginConfig !== void 0 ? pluginConfig : {},
    allowControls = _ref2.allowControls;
  var stickyHeader = useInternalTablePluginStateSelector(api, 'stickyHeader', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var dragMenuDirection = useInternalTablePluginStateSelector(api, 'dragMenuDirection', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var dragMenuIndex = useInternalTablePluginStateSelector(api, 'dragMenuIndex', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isDragMenuOpen = useInternalTablePluginStateSelector(api, 'isDragMenuOpen', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, targetCellPosition && (tableRef || isCellMenuOpenByKeyboard) && !isResizing && options && options.allowContextualMenu && /*#__PURE__*/React.createElement(FloatingContextualButton, {
    isNumberColumnEnabled: tableNode && tableNode.attrs.isNumberColumnEnabled,
    editorView: editorView,
    tableNode: tableNode,
    mountPoint: popupsMountPoint,
    targetCellPosition: targetCellPosition,
    scrollableElement: popupsScrollableElement,
    dispatchAnalyticsEvent: dispatchAnalyticsEvent,
    isContextualMenuOpen: isContextualMenuOpen,
    stickyHeader: stickyHeader,
    tableWrapper: tableWrapperTarget,
    isCellMenuOpenByKeyboard: isCellMenuOpenByKeyboard
  }), allowControls && /*#__PURE__*/React.createElement(FloatingInsertButton, {
    tableNode: tableNode,
    tableRef: tableRef,
    insertColumnButtonIndex: insertColumnButtonIndex,
    insertRowButtonIndex: insertRowButtonIndex,
    isHeaderColumnEnabled: isHeaderColumnEnabled,
    isHeaderRowEnabled: isHeaderRowEnabled,
    isDragAndDropEnabled: isDragAndDropEnabled,
    isTableScalingEnabled: options === null || options === void 0 ? void 0 : options.isTableScalingEnabled,
    editorView: editorView,
    mountPoint: popupsMountPoint,
    boundariesElement: popupsBoundariesElement,
    scrollableElement: popupsScrollableElement,
    hasStickyHeaders: stickyHeader && stickyHeader.sticky,
    dispatchAnalyticsEvent: dispatchAnalyticsEvent,
    editorAnalyticsAPI: editorAnalyticsAPI,
    getEditorContainerWidth: defaultGetEditorContainerWidth,
    getEditorFeatureFlags: (options === null || options === void 0 ? void 0 : options.getEditorFeatureFlags) || defaultGetEditorFeatureFlags,
    isChromelessEditor: options === null || options === void 0 ? void 0 : options.isChromelessEditor,
    api: api,
    isCommentEditor: options === null || options === void 0 ? void 0 : options.isCommentEditor
  }), (options === null || options === void 0 ? void 0 : options.allowContextualMenu) && /*#__PURE__*/React.createElement(FloatingContextualMenu, {
    editorView: editorView,
    mountPoint: popupsMountPoint,
    boundariesElement: popupsBoundariesElement,
    targetCellPosition: targetCellPosition,
    isOpen: Boolean(isContextualMenuOpen) && !isResizing,
    pluginConfig: pluginConfig,
    editorAnalyticsAPI: editorAnalyticsAPI,
    getEditorContainerWidth: defaultGetEditorContainerWidth,
    getEditorFeatureFlags: (options === null || options === void 0 ? void 0 : options.getEditorFeatureFlags) || defaultGetEditorFeatureFlags,
    isCellMenuOpenByKeyboard: isCellMenuOpenByKeyboard,
    isCommentEditor: options === null || options === void 0 ? void 0 : options.isCommentEditor,
    api: api
  }), isDragAndDropEnabled && /*#__PURE__*/React.createElement(FloatingDragMenu, {
    editorView: editorView,
    mountPoint: popupsMountPoint,
    boundariesElement: popupsBoundariesElement,
    tableRef: tableRef,
    tableNode: tableNode,
    targetCellPosition: targetCellPosition,
    direction: dragMenuDirection,
    index: dragMenuIndex,
    isOpen: !!isDragMenuOpen && !isResizing,
    getEditorContainerWidth: defaultGetEditorContainerWidth,
    editorAnalyticsAPI: editorAnalyticsAPI,
    stickyHeaders: stickyHeader,
    pluginConfig: pluginConfig,
    isTableScalingEnabled: options === null || options === void 0 ? void 0 : options.isTableScalingEnabled,
    getEditorFeatureFlags: (options === null || options === void 0 ? void 0 : options.getEditorFeatureFlags) || defaultGetEditorFeatureFlags,
    ariaNotifyPlugin: ariaNotifyPlugin,
    api: api,
    isCommentEditor: options === null || options === void 0 ? void 0 : options.isCommentEditor
  }), allowControls && !isDragAndDropEnabled && !isResizing && /*#__PURE__*/React.createElement(FloatingDeleteButton, {
    editorView: editorView,
    selection: editorView.state.selection,
    tableRef: tableRef,
    mountPoint: popupsMountPoint,
    boundariesElement: popupsBoundariesElement,
    scrollableElement: popupsScrollableElement,
    stickyHeaders: stickyHeader,
    isNumberColumnEnabled: tableNode && tableNode.attrs.isNumberColumnEnabled,
    editorAnalyticsAPI: editorAnalyticsAPI,
    api: api
  }), ((options === null || options === void 0 ? void 0 : options.isTableScalingEnabled) || (options === null || options === void 0 ? void 0 : options.tableOptions.allowTableResizing) && options.isCommentEditor) && isTableResizing && widthToWidest && resizingTableLocalId && resizingTableRef && widthToWidest[resizingTableLocalId] && /*#__PURE__*/React.createElement(FloatingToolbarLabel, {
    target: resizingTableRef,
    content: /*#__PURE__*/React.createElement(FullWidthDisplay, null),
    alignX: 'center',
    alignY: 'bottom',
    stick: true,
    forcePlacement: true,
    zIndex: akEditorFloatingPanelZIndex,
    offset: [0, 10]
  }));
};
export var ContentComponent = function ContentComponent(_ref3) {
  var _api$featureFlags;
  var api = _ref3.api,
    editorView = _ref3.editorView,
    dispatchAnalyticsEvent = _ref3.dispatchAnalyticsEvent,
    options = _ref3.options,
    popupsMountPoint = _ref3.popupsMountPoint,
    popupsBoundariesElement = _ref3.popupsBoundariesElement,
    popupsScrollableElement = _ref3.popupsScrollableElement,
    defaultGetEditorContainerWidth = _ref3.defaultGetEditorContainerWidth,
    defaultGetEditorFeatureFlags = _ref3.defaultGetEditorFeatureFlags;
  return /*#__PURE__*/React.createElement(ErrorBoundary, {
    component: ACTION_SUBJECT.TABLES_PLUGIN,
    dispatchAnalyticsEvent: dispatchAnalyticsEvent,
    fallbackComponent: null
  }, /*#__PURE__*/React.createElement(GlobalStylesWrapper, {
    featureFlags: api === null || api === void 0 || (_api$featureFlags = api.featureFlags) === null || _api$featureFlags === void 0 ? void 0 : _api$featureFlags.sharedState.currentState(),
    isDragAndDropEnabledOption: options === null || options === void 0 ? void 0 : options.dragAndDropEnabled,
    api: api
  }), /*#__PURE__*/React.createElement(ContentComponentInternal, {
    api: api,
    editorView: editorView,
    dispatchAnalyticsEvent: dispatchAnalyticsEvent,
    options: options,
    popupsMountPoint: popupsMountPoint,
    popupsBoundariesElement: popupsBoundariesElement,
    popupsScrollableElement: popupsScrollableElement,
    defaultGetEditorContainerWidth: defaultGetEditorContainerWidth,
    defaultGetEditorFeatureFlags: defaultGetEditorFeatureFlags
  }));
};